import { useEffect, useState } from "react";
import { Loader2, Users, TrendingUp, TrendingDown, ChevronDown, ChevronUp, User } from "lucide-react";
import { Button } from "../ui/button";
import NovaCriteriaRadar from "./NovaCriteriaRadar";
import NovaCriteriaRadarCompare from "./NovaCriteriaRadarCompare";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * NovaVoterCoherencePanel — coherencia cross-campaña por miembro del comité.
 * Muestra, por votante, media por criterio + generosidad (avg propio - avg grupo) + retos votados.
 */
export default function NovaVoterCoherencePanel({ campaignId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedPanel, setExpandedPanel] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/nova/campaigns/${campaignId}/voters-coherence`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  };

  useEffect(() => { if (expandedPanel) load(); }, [campaignId, expandedPanel]);

  const voters = data?.voters || [];
  const criteria = data?.criteria || [];

  // Build a "campaign average" score object to compare against each voter
  const groupScore = data ? { count: data.total_votes, overall: 0, per_criterion: Object.fromEntries((criteria).map(c => [c.key, { avg: data.campaign_averages[c.key], n: 0 }])) } : null;

  return (
    <div className="rounded-2xl border border-sky-300 dark:border-sky-700 bg-sky-50/40 dark:bg-sky-500/5 overflow-hidden" data-testid="voter-coherence-panel">
      <button
        onClick={() => setExpandedPanel(!expandedPanel)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-sky-100/40 dark:hover:bg-sky-500/10 transition-colors"
        data-testid="voter-coherence-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-sky-500 text-white flex items-center justify-center">
            <Users className="w-4 h-4"/>
          </div>
          <div className="text-left">
            <div className="text-sm font-black text-sky-900 dark:text-sky-200">Coherencia por votante</div>
            <div className="text-[11px] text-muted-foreground">Media por criterio, generosidad y retos votados por cada miembro</div>
          </div>
        </div>
        {expandedPanel ? <ChevronUp className="w-4 h-4"/> : <ChevronDown className="w-4 h-4"/>}
      </button>

      {expandedPanel && (
        <div className="border-t border-sky-200 dark:border-sky-800 p-4 space-y-3">
          {loading && <div className="text-sm text-muted-foreground py-4">Analizando coherencia…</div>}
          {!loading && !voters.length && (
            <div className="text-center text-muted-foreground text-xs py-4">Aún no hay votos en la campaña.</div>
          )}
          {!loading && voters.length > 0 && (
            <>
              <div className="text-[11px] text-muted-foreground">
                <strong>{voters.length}</strong> votante{voters.length !== 1 ? "s" : ""} han emitido un total de <strong>{data.total_votes}</strong> votos. La generosidad compara la media personal frente a la media del grupo.
              </div>
              <div className="space-y-2">
                {voters.map((voter, idx) => {
                  const isOpen = expandedId === voter.user_id;
                  const generosityBg = voter.generosity > 0.3 ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                    : voter.generosity < -0.3 ? "bg-red-500/15 text-red-700 dark:text-red-400"
                    : "bg-slate-500/15 text-slate-700 dark:text-slate-300";
                  const voterScore = {
                    count: voter.votes_count,
                    overall: 0,
                    per_criterion: Object.fromEntries(criteria.map(c => [c.key, { avg: voter.avg_per_criterion?.[c.key]?.avg || 0, n: voter.avg_per_criterion?.[c.key]?.n || 0 }])),
                  };
                  return (
                    <div key={voter.user_id} className="rounded-xl border border-sky-200 dark:border-sky-800/60 bg-white dark:bg-slate-900/60 overflow-hidden" data-testid={`voter-row-${idx}`}>
                      <button
                        onClick={() => setExpandedId(isOpen ? null : voter.user_id)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-sky-50/60 dark:hover:bg-sky-500/10 transition-colors"
                        data-testid={`voter-row-toggle-${idx}`}
                      >
                        <User className="w-4 h-4 text-sky-600 flex-shrink-0"/>
                        <div className="flex-1 min-w-0 text-left">
                          <div className="text-sm font-semibold truncate">{voter.user_name}</div>
                          <div className="text-[10px] text-muted-foreground">{voter.votes_count} voto{voter.votes_count !== 1 ? "s" : ""}</div>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-1 ${generosityBg}`} title="Media propia vs media del grupo">
                          {voter.generosity > 0.3 ? <TrendingUp className="w-3 h-3"/> : voter.generosity < -0.3 ? <TrendingDown className="w-3 h-3"/> : null}
                          {voter.generosity > 0 ? "+" : ""}{voter.generosity}
                        </span>
                        {isOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground"/> : <ChevronDown className="w-4 h-4 text-muted-foreground"/>}
                      </button>
                      {isOpen && (
                        <div className="border-t border-sky-200 dark:border-sky-800 p-3 space-y-3" data-testid={`voter-detail-${idx}`}>
                          <NovaCriteriaRadarCompare
                            items={voters.length > 1 ? [
                              { label: voter.user_name, score: voterScore, color: "#0ea5e9" },
                              { label: "Media grupal", score: groupScore, color: "#94a3b8" },
                            ] : [
                              { label: voter.user_name, score: voterScore, color: "#0ea5e9" },
                            ]}
                            criteria={criteria}
                            height={260}
                          />
                          {voters.length === 1 && (
                            <div className="text-[10px] text-muted-foreground italic text-center">Necesitas más votantes para comparar coherencia entre miembros del comité.</div>
                          )}
                          <div>
                            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Retos votados ({voter.challenges_voted.length})</div>
                            <div className="rounded-lg border border-slate-200 dark:border-slate-800 overflow-hidden divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                              {voter.challenges_voted.map((ch, i) => (
                                <div key={i} className="px-2 py-1.5 flex items-center gap-2 hover:bg-slate-50 dark:hover:bg-slate-900/60" data-testid={`voter-vote-${idx}-${i}`}>
                                  <code className="text-[9px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">#{ch.n}</code>
                                  <span className="flex-1 truncate">{ch.title}</span>
                                  {ch.area && <span className="text-[10px] text-muted-foreground">{ch.area}</span>}
                                  <span className="text-[10px] font-bold text-sky-700 dark:text-sky-300">{ch.overall}/5</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
